import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  CreateEpisodeFormValues,
  formatReasonCode,
  reasonCodeOptions,
} from "../../lib/intakeForm";
import FormControl from "../form/FormControl";
import Input from "../form/Input";

type RecordDetailsFormProps = {
  form: UseFormReturn<CreateEpisodeFormValues>;
  stepLabel?: string;
  showCommentField?: boolean;
  commentLength?: number;
  showVerifiedField?: boolean;
};

function RecordDetailsForm({
  form,
  stepLabel = "Episode details",
  showCommentField = false,
  commentLength = 0,
  showVerifiedField = false,
}: RecordDetailsFormProps) {
  const { t } = useTranslation();
  return (
    <section className="subCard" aria-labelledby="record-details-heading">
      <h3 id="record-details-heading" className="subCardTitle">
        {t(stepLabel)}
      </h3>

      <div className="formColumns">
        <FormControl<CreateEpisodeFormValues>
          label={t("Reported at")}
          name="reportedAt"
        >
          <Input<CreateEpisodeFormValues>
            name="reportedAt"
            type="datetime-local"
            rules={{ required: t("Reported at is required") }}
          />
        </FormControl>

        <FormControl<CreateEpisodeFormValues>
          label={t("Start date")}
          name="startDate"
        >
          <Input<CreateEpisodeFormValues>
            name="startDate"
            type="date"
            rules={{ required: t("Start date is required") }}
          />
        </FormControl>

        <FormControl<CreateEpisodeFormValues>
          label={t("Estimated return date")}
          name="estimatedReturnDate"
        >
          <Input<CreateEpisodeFormValues>
            name="estimatedReturnDate"
            type="date"
            rules={{ required: t("Estimated return date is required") }}
          />
        </FormControl>

        <FormControl<CreateEpisodeFormValues>
          label={t("Return date")}
          name="returnDate"
        >
          <Input<CreateEpisodeFormValues> name="returnDate" type="date" />
        </FormControl>

        <FormControl<CreateEpisodeFormValues>
          label={t("Reason code")}
          name="reasonCode"
        >
          <select
            className="inputControl"
            {...form.register("reasonCode", {
              required: t("Reason code is required"),
            })}
          >
            <option value="">{t("Select reason code")}</option>
            {reasonCodeOptions.map((reasonCode) => (
              <option key={reasonCode} value={reasonCode}>
                {formatReasonCode(reasonCode)}
              </option>
            ))}
          </select>
        </FormControl>

        <label className="field checkboxField">
          <span className="fieldLabel">{t("Musculoskeletal issue")}</span>
          <span className="checkboxRow">
            <input
              id="episode-musculoskeletal"
              type="checkbox"
              {...form.register("musculoskeletal")}
            />
            <span className="checkboxRowText">
              {t("The incapacity is due to a musculoskeletal issue")}
            </span>
          </span>
        </label>

        {showVerifiedField ? (
          <label className="field checkboxField">
            <span className="fieldLabel">
              {t("Incapacity certified by Akkilles")}
            </span>
            <span className="checkboxRow">
              <input
                id="episode-verified"
                type="checkbox"
                {...form.register("verified")}
              />
              <span className="checkboxRowText">
                {t("Incapacity has been certified by Akkilles")}
              </span>
            </span>
          </label>
        ) : null}
      </div>

      {showCommentField ? (
        <>
          <FormControl<CreateEpisodeFormValues>
            label={t("Initial comment")}
            name="comment"
          >
            <textarea
              className="inputControl textareaControl"
              maxLength={2000}
              rows={4}
              {...form.register("comment", {
                maxLength: {
                  value: 2000,
                  message: t("Comment cannot exceed 2000 characters"),
                },
              })}
            />
          </FormControl>

          <p className="hintText sectionHint">
            {t("Do not enter unnecessary sensitive medical details.")}
          </p>
          <p className="hintText sectionHint" aria-live="polite">
            {t("{{count}}/2000 characters", { count: commentLength })}
          </p>
        </>
      ) : null}
    </section>
  );
}

export default RecordDetailsForm;
